<template>
  <div class="signup-page-shell">
    <main class="signup-page-main">
      <div class="signup-page-card">
        <BrandLogo class="signup-page-logo" />
        <!-- El alta es solo por invitación: sin una válida, se pide acceso. -->
        <RequestAccess v-if="mostrarSolicitud">
          <InvitationBanner :dead="invitationDead" />
        </RequestAccess>
        <template v-else>
          <h1 class="signup-page-heading">{{ $t('invitation.heading') }}</h1>
          <InvitationBanner :preview="invitation" />

          <v-form ref="form" v-model="valid" @submit.prevent="onSubmit">
            <v-text-field
              v-model="email"
              type="email"
              :label="$t('signupHero.email')"
              outlined
              dense
              persistent-placeholder
              autocomplete="email"
              :readonly="!!invitation"
              :hint="invitation ? $t('invitation.emailLocked') : ''"
              :persistent-hint="!!invitation"
              :rules="emailRules"
              class="signup-page-input"
              data-test="signup-email"
            />

            <v-btn
              type="submit"
              block
              depressed
              large
              :loading="loading"
              :disabled="!valid"
              class="signup-page-submit"
            >
              {{ $t('signupHero.submit') }}
            </v-btn>
          </v-form>

          <p class="signup-page-footer">
            {{ $t('signupHero.haveAccount') }}
            <router-link :to="signInPath" class="signup-page-link">
              {{ $t('signupHero.signIn') }}
              <span aria-hidden="true" class="signup-page-arrow">↗</span>
            </router-link>
          </p>
        </template>
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { okeyTrack } from '../utils/track';
import InvitationBanner from '../invitations/InvitationBanner.vue';
import { INVITATION_PARAM, invitationTokenFromUrl, previewInvitation, withInvitationInApp, type InvitationPreview } from '../invitations/invitation';
import { paramDeLaUrl } from '../utils/urlParams';
import BrandLogo from '@/components/brand/BrandLogo.vue';
import RequestAccess from '@/components/RequestAccess.vue';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default defineComponent({
  name: 'SignupView',
  components: { BrandLogo, InvitationBanner, RequestAccess },
  data() {
    return {
      email: '' as string,
      invitationToken: null as string | null,
      invitation: null as InvitationPreview | null,
      invitationDead: false as boolean,
      valid: false as boolean,
      loading: false as boolean,
      emailRules: [
        (v: string): boolean | string => !!v || String(this.$t('signupHero.emailRequired')),
        (v: string): boolean | string => EMAIL_RE.test(v) || String(this.$t('signupHero.emailInvalid')),
      ],
    };
  },
  computed: {
    /** Sin token, o con uno que el backend ya no reconoce: no hay alta posible. */
    mostrarSolicitud(): boolean {
      return !this.invitationToken || this.invitationDead;
    },
    signInPath(): string {
      return withInvitationInApp('/signin', this.invitationToken, this.invitation ? { email: this.invitation.email } : {});
    },
  },
  created(): void {
    // Síncrono: quien trae invitación no ve parpadear «Solicitar acceso»
    // mientras el backend contesta la vista previa.
    this.invitationToken = invitationTokenFromUrl();
  },
  async mounted(): Promise<void> {
    try {
      const url = new URL(window.location.href);
      const prefill = paramDeLaUrl('email');
      if (prefill) this.email = prefill;
      if (this.invitationToken) {
        // Quién invita a quién: el correo invitado manda sobre cualquier prerrelleno.
        this.invitation = await previewInvitation(this.invitationToken);
        if (this.invitation) this.email = this.invitation.email;
        else this.invitationDead = true;
      }
      // auth_page_view: llegada a una pantalla de auth. GA4 ya emite page_view
      // por sí solo; este evento añade QUÉ pantalla y de qué CTA/plan viene,
      // que es lo que permite localizar el abandono. Sin PII.
      okeyTrack('auth_page_view', {
        screen: 'signup',
        plan_interest: url.searchParams.get('plan_interest') || 'none',
        prefilled: !!prefill,
      });
    } catch {
      // ignore
    }
  },
  methods: {
    async onSubmit(): Promise<void> {
      const valid = (this.$refs.form as unknown as { validate(): boolean }).validate();
      if (!valid) return;
      this.loading = true;
      const params = new URLSearchParams(window.location.search);
      // El email viaja en la URL hacia /register solo para prerrellenar el campo:
      // nunca se emite como propiedad de evento, y `public/auth-scrub.js` lo
      // retira de la barra de direcciones antes de que la analítica lo lea. La
      // invitación ya no está en `location.search` por lo mismo: viaja a mano.
      params.set('email', this.email.trim());
      if (this.invitationToken) params.set(INVITATION_PARAM, this.invitationToken);
      // Paso 1 de 2 superado. Separa "llegó al alta" de "empezó a darse de alta".
      okeyTrack('signup_submit_attempt', {
        screen: 'signup',
        step: 'email',
        plan_interest: params.get('plan_interest') || 'none',
      });
      window.location.assign(`/register?${params.toString()}`);
    },
  },
});
</script>

<style scoped>
.signup-page-shell {
  --ink:        var(--ii-ink);
  --ink-hushed: var(--ii-ink-2);
  --border:     var(--ii-rule);
  min-height: 100vh;
  background: var(--casa-fondo);
  color: var(--ink);
  font-family: var(--ii-display);
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  flex-direction: column;
}

.signup-page-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--casa-espacio-8) var(--casa-espacio-6) 64px;
}

.signup-page-card {
  width: 100%;
  max-width: 460px;
}

.signup-page-logo { margin-bottom: var(--casa-espacio-2); }

.signup-page-heading {
  margin: 0 0 var(--casa-espacio-3);
  font-family: inherit;
  font-size: 40px;
  line-height: 1.0;
  font-weight: 500;
  letter-spacing: -0.04em;
  color: var(--ink);
  text-wrap: balance;
}
@media (min-width: 768px) {
  .signup-page-heading { font-size: 48px; }
}

.signup-page-input { margin-bottom: var(--casa-espacio-1); }

.signup-page-submit {
  margin-top: var(--casa-espacio-3);
  height: 46px !important;
  background-color: var(--ii-accent) !important;
  color: var(--casa-texto-inverso) !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  font-size: var(--casa-letra-cuerpo) !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  border-radius: var(--casa-radio-tarjeta) !important;
  box-shadow: none !important;
}
.signup-page-submit:hover { background-color: var(--ii-accent-2) !important; }
.signup-page-submit:disabled {
  background-color: var(--casa-fondo-app) !important;
  color: var(--casa-texto-tenue) !important;
}

.signup-page-footer {
  margin-top: var(--casa-espacio-8);
  font-family: inherit;
  font-size: var(--casa-letra-texto);
  font-weight: 400;
  color: var(--ink-hushed);
  line-height: 1.5;
}
.signup-page-link {
  color: var(--ink);
  text-decoration: underline;
  text-underline-offset: 2px;
  font-weight: 400;
}
.signup-page-arrow {
  display: inline-block;
  margin-left: var(--casa-espacio-0-5);
  transform: translateY(-1px);
}

.signup-page-input :deep(.v-input__slot) {
  min-height: 46px !important;
  border-radius: var(--casa-radio-tarjeta) !important;
}
.signup-page-input :deep(fieldset) {
  border-color: var(--border) !important;
  border-width: 1px !important;
}
.signup-page-input :deep(.v-text-field--outlined.v-input--is-focused fieldset) {
  border-color: var(--ii-accent) !important;
  border-width: 1px !important;
}
.signup-page-input :deep(.v-label) {
  color: var(--ink-hushed) !important;
  font-family: var(--ii-display) !important;
  font-weight: 400 !important;
  font-size: var(--casa-letra-cuerpo) !important;
}
.signup-page-input :deep(input) {
  font-family: var(--ii-display) !important;
  font-weight: 400 !important;
  font-size: var(--casa-letra-cuerpo) !important;
  padding: var(--casa-espacio-2-5) var(--casa-espacio-1) !important;
  color: var(--ink) !important;
}

@media (max-width: 480px) {
  .signup-page-heading { font-size: 36px; }
  .signup-page-main { padding: var(--casa-espacio-6) var(--casa-espacio-5); }
}
</style>
